import {FiArrowRight, FiClock, FiRefreshCw} from 'react-icons/fi';
import {
  goalHealthBarOrder,
  goalListStatusBarColors,
  goalListStatusBarLabels,
} from '@ui/style/goal-status';
import {MyGoalsLifecycleFilter} from '../my-objective/my-goals-v2.types';
import {summarizeMyGoals} from '../my-objective/my-goals-v2.utils';
import {
  DueSoon,
  OverdueCarryover,
  LifecycleButton,
  LifecycleTabs,
  StatusBar,
  StatusLegend,
  StatusReviewAction,
  StatusReviewNotice,
  StatusSegment,
  StatusSummary,
  SummaryLabel,
  SummaryPanel,
  SummaryProgress,
} from '../my-objective/my-goals-v2.styles';
import {ProgressRing} from '../my-objective/progress-ring';

const lifecycleLabels: Array<{
  value: MyGoalsLifecycleFilter;
  label: string;
}> = [
  {value: 'all', label: 'All'},
  {value: 'active', label: 'Active'},
  {value: 'ended', label: 'Ended'},
  {value: 'overdue', label: 'Overdue'},
];

export const TeamGoalsOverview = ({
  summary,
  carryoverCount = 0,
  lifecycle,
  statusReviewCount,
  reviewSuggestedOnly,
  pageLabel = 'Team Goals',
  onLifecycleChange,
  onReview,
}: {
  summary: ReturnType<typeof summarizeMyGoals>;
  carryoverCount?: number;
  lifecycle: MyGoalsLifecycleFilter;
  statusReviewCount: number;
  reviewSuggestedOnly: boolean;
  pageLabel?: string;
  onLifecycleChange: (value: MyGoalsLifecycleFilter) => void;
  onReview: () => void;
}) => {
  const lifecycleCounts: Record<MyGoalsLifecycleFilter, number> = {
    all: summary.total + carryoverCount,
    active: summary.active,
    ended: summary.ended,
    overdue: summary.overdue + carryoverCount,
  };
  const healthTotal = goalHealthBarOrder.reduce(
    (total, status) => total + summary.byHealth[status],
    0,
  );

  return (
    <SummaryPanel aria-label={`${pageLabel} summary`}>
      <SummaryProgress>
        <ProgressRing progress={summary.averageProgress} />
        <SummaryLabel>
          Avg. progress
          <strong>
            {summary.total} goal{summary.total === 1 ? '' : 's'}
          </strong>
        </SummaryLabel>
      </SummaryProgress>

      <StatusSummary>
        <h2>Goal health this period</h2>
        <StatusBar aria-label="Goals by health">
          {goalHealthBarOrder.map((status) => (
            <StatusSegment
              key={status}
              $color={goalListStatusBarColors[status]}
              $width={
                healthTotal ? (summary.byHealth[status] / healthTotal) * 100 : 0
              }
              title={`${summary.byHealth[status]} ${goalListStatusBarLabels[status]}`}
            />
          ))}
        </StatusBar>
        <StatusLegend>
          {goalHealthBarOrder.map((status) => (
            <span key={status}>
              <i style={{background: goalListStatusBarColors[status]}} />
              <strong>{summary.byHealth[status]}</strong>{' '}
              {goalListStatusBarLabels[status]}
            </span>
          ))}
        </StatusLegend>
        {statusReviewCount > 0 && (
          <StatusReviewNotice $active={reviewSuggestedOnly}>
            <div>
              <FiRefreshCw aria-hidden="true" />
              <span>
                <strong>{statusReviewCount}</strong>{' '}
                {statusReviewCount === 1 ? 'goal may' : 'goals may'} need a
                status review
              </span>
            </div>
            <StatusReviewAction
              type="button"
              disabled={reviewSuggestedOnly}
              onClick={onReview}>
              {reviewSuggestedOnly ? 'Reviewing' : 'Review'}
              {!reviewSuggestedOnly && <FiArrowRight aria-hidden="true" />}
            </StatusReviewAction>
          </StatusReviewNotice>
        )}
        {summary.dueSoon > 0 && (
          <DueSoon>
            <FiClock /> {summary.dueSoon} due within 14 days
          </DueSoon>
        )}
        {carryoverCount > 0 && (
          <OverdueCarryover>
            <FiClock /> {carryoverCount} overdue from earlier periods
          </OverdueCarryover>
        )}
      </StatusSummary>

      <LifecycleTabs aria-label={`Filter ${pageLabel} by lifecycle`}>
        {lifecycleLabels.map((item) => (
          <LifecycleButton
            key={item.value}
            type="button"
            $active={lifecycle === item.value}
            onClick={() => onLifecycleChange(item.value)}>
            {item.label}
            <span>{lifecycleCounts[item.value]}</span>
          </LifecycleButton>
        ))}
      </LifecycleTabs>
    </SummaryPanel>
  );
};
